import React, { useState } from 'react';
import {
  Box, Typography, Card, Grid, Chip,
  Stack, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Popover, IconButton, Paper,
} from '@mui/material';
import {
  MonetizationOn, Shield, TrendingDown, Equalizer, Scale,
  HelpOutline, InfoOutlined, WarningAmber,
  AutoFixHigh, Lightbulb, ArrowUpward, ArrowDownward,
} from '@mui/icons-material';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
} from 'recharts';
import type { Scan, Issue } from '../../../store/api/githubApi';
import { useGetScanHistoryQuery } from '../../../store/api/githubApi';
import { CATEGORIES } from '../constants';
import { scoreColor, scoreGrade, calcBusinessImpact, fmtMoney, tdrGrade, tdrColor } from '../utils';

// Generic, honest descriptions per category — never a specific claim about what this
// repository's code actually does, since nothing here is derived from reading the code itself.
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  security: 'Security vulnerabilities and exposure patterns flagged by static analysis.',
  complexity: 'High cyclomatic complexity and oversized functions that are costly to maintain.',
  documentation: 'Missing or incomplete documentation on public interfaces.',
  dead_code: 'Unused code increasing maintenance surface without functional value.',
  dependencies: 'Vulnerable or outdated third-party dependencies.',
};

// Maps a score_formula weight key to the Scan field holding its real score and a short,
// honest description. Used to render the "how is this calculated" dialog from real data
// instead of hardcoded percentages — see docs/scan-report-integrity-plan.md, KG-04.
const FORMULA_DIMENSION_META: Record<string, { label: string; scoreField: keyof Scan; description: string }> = {
  security: { label: 'Security', scoreField: 'score_security', description: 'SAST vulnerability patterns and CVE dependency exposure.' },
  complexity: { label: 'Complexity', scoreField: 'score_complexity', description: 'Cyclomatic complexity, nested branches, and maintainability.' },
  dependencies: { label: 'Dependencies', scoreField: 'score_dependencies', description: 'Vulnerable or outdated third-party packages (CVE exposure).' },
  dead_code: { label: 'Dead Code', scoreField: 'score_dead_code', description: 'Unused code increasing maintenance surface.' },
  documentation: { label: 'Documentation', scoreField: 'score_documentation', description: 'Docstring/comment coverage on public interfaces.' },
};

function topCategoriesByCount(byCategory: Record<string, Issue[]>, limit: number) {
  return Object.entries(byCategory)
    .map(([key, items]) => ({ key, count: items.length }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map((c) => ({
      ...c,
      label: CATEGORIES.find((cat) => cat.issueKey === c.key)?.label ?? c.key,
      description: CATEGORY_DESCRIPTIONS[c.key] ?? 'Findings detected by static analysis in this category.',
    }));
}

// ── 1. Codebase Health KPI Card (Row 1, Col 1: ~30%) ─────────────────────────

const CodebaseHealthKPI: React.FC<{
  score: number | null;
  previousScore: number | null;
  onOpenScoreCalc: () => void;
}> = ({ score, previousScore, onOpenScoreCalc }) => {
  const val = score ?? 0;
  const grade = scoreGrade(val);
  const color = scoreColor(val);

  const riskDescription = val >= 75
    ? 'Your codebase is in healthy condition with minimal debt.'
    : val >= 50
      ? 'Your codebase has significant technical-debt and maintainability risks.'
      : 'Your codebase has critical maintainability and technical-debt risks.';

  // 0–10 -> 1 segment, 11–20 -> 2 segments, ..., 91–100 -> 10 segments
  const filledSegments = val === 0 ? 0 : Math.min(10, Math.max(1, Math.ceil(val / 10)));

  return (
    <Card
      elevation={0}
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2.5,
        height: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Title */}
      <Typography
        variant="caption"
        fontWeight={800}
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}
      >
        CODEBASE HEALTH
      </Typography>

      {/* 1 & 2. Score & Grade */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography variant="h3" fontWeight={900} sx={{ color, fontSize: { xs: '2.2rem', sm: '2.5rem' }, lineHeight: 1 }}>
            {val}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="text.secondary" sx={{ opacity: 0.7 }}>
            / 100
          </Typography>
        </Box>
        <Typography variant="h3" fontWeight={900} sx={{ color, fontSize: { xs: '2rem', sm: '2.3rem' }, lineHeight: 1 }}>
          {grade}
        </Typography>
      </Box>

      {/* 3. Horizontal Segmented Health Meter */}
      <Box sx={{ display: 'flex', gap: 0.75, width: '100%', mb: 1, alignItems: 'center' }}>
        {Array.from({ length: 10 }).map((_, idx) => {
          const isFilled = idx < filledSegments;
          return (
            <Box
              key={idx}
              sx={{
                flex: 1,
                height: 10,
                borderRadius: 5,
                bgcolor: isFilled ? color : '#e2e8f0',
                transition: 'background-color 0.3s ease',
              }}
            />
          );
        })}
      </Box>

      {/* 4. Health Scale Labels */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: val < 50 ? '#ef4444' : 'text.secondary', fontSize: '0.725rem' }}>
          Critical
        </Typography>
        <Typography variant="caption" fontWeight={700} sx={{ color: val >= 50 && val < 75 ? '#f59e0b' : 'text.secondary', fontSize: '0.725rem' }}>
          Needs work
        </Typography>
        <Typography variant="caption" fontWeight={700} sx={{ color: val >= 75 ? '#10b981' : 'text.secondary', fontSize: '0.725rem' }}>
          Healthy
        </Typography>
      </Box>

      {/* 5. Previous scan comparison — real delta via useGetScanHistoryQuery, or nothing on a
          first scan. See docs/scan-report-integrity-plan.md, KG-03. */}
      {previousScore != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          {(() => {
            const delta = Math.round(val - previousScore);
            const Icon = delta >= 0 ? ArrowUpward : ArrowDownward;
            return (
              <>
                <Icon sx={{ fontSize: 14, color: delta >= 0 ? 'success.main' : 'error.main' }} />
                <Typography variant="caption" fontWeight={800} color={delta >= 0 ? 'success.main' : 'error.main'} sx={{ fontSize: '0.775rem' }}>
                  {delta >= 0 ? `↑ +${delta}` : `↓ ${delta}`} points from previous scan
                </Typography>
              </>
            );
          })()}
        </Box>
      )}

      {/* 6. Supporting explanation */}
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.45, mb: 2 }}>
        {riskDescription}
      </Typography>

      {/* 7. Score calculation link */}
      <Button
        size="small"
        variant="text"
        color="primary"
        startIcon={<HelpOutline fontSize="small" />}
        onClick={onOpenScoreCalc}
        sx={{ fontWeight: 800, fontSize: 11, textTransform: 'none', mt: 'auto', alignSelf: 'flex-start', px: 0 }}
      >
        How is this score calculated?
      </Button>
    </Card>
  );
};

// ── 2. Health Radar Component ──────────────────────────────────────────────────

const HealthRadar: React.FC<{ scan: Scan }> = ({ scan }) => {
  const data = [
    { subject: 'Security', A: scan.score_security ?? 0 },
    { subject: 'Complexity', A: scan.score_complexity ?? 0 },
    { subject: 'Docs', A: scan.score_documentation ?? 0 },
    { subject: 'Coverage', A: scan.score_coverage ?? 0 },
  ];
  return (
    <ResponsiveContainer width="100%" height={175}>
      <RadarChart data={data} margin={{ top: 10, right: 15, bottom: 10, left: 15 }}>
        <PolarGrid stroke="#e5e7eb" />
        {/* @ts-expect-error recharts type overload */}
        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#6b7280', fontWeight: 600 }} />
        <Radar dataKey="A" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} strokeWidth={2} />
      </RadarChart>
    </ResponsiveContainer>
  );
};

// ── 3. Main Executive Summary Component ────────────────────────────────────────

const ExecutiveSummary: React.FC<{
  scan: Scan;
  issues: Issue[];
  byCategory: Record<string, Issue[]>;
  activeCategory: string | null;
  onCategoryClick: (key: string) => void;
  onNavigate?: (path: string) => void;
  repoName?: string | null;
}> = ({ scan, issues, byCategory, activeCategory, onCategoryClick, onNavigate, repoName }) => {
  // Modal & Popover States
  const [scoreCalcOpen, setScoreCalcOpen] = useState(false);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [impactPopoverAnchor, setImpactPopoverAnchor] = useState<{ element: HTMLElement; cardKey: string } | null>(null);

  const impact = calcBusinessImpact(issues);

  // Strict Data Consistency (Requirement 2)
  const totalIssueCount = issues.length;
  const criticalCount = issues.filter((i) => i.severity === 'critical').length;
  const highCount = issues.filter((i) => i.severity === 'high').length;
  const mediumCount = issues.filter((i) => i.severity === 'medium').length;
  const lowCount = issues.filter((i) => i.severity === 'low').length;

  const secCount = issues.filter((i) => i.category === 'security').length;
  const cveCount = issues.filter((i) => i.category === 'dependencies').length;

  // Security-specific critical count — distinct from criticalCount (all categories) above.
  // Only this one is used where the label itself says "vulnerability" findings.
  const criticalVulnCount = issues.filter(
    (i) => i.severity === 'critical' && (i.category === 'security' || i.category === 'dependencies')
  ).length;

  const topCategories = topCategoriesByCount(byCategory, 4);

  // Real previous-scan score for the delta shown on the Codebase Health card — null when
  // this is the first comparable scan (history excludes PR-check scans, which are deltas,
  // not full-repo scores). See docs/scan-report-integrity-plan.md, KG-03.
  const { data: scanHistory } = useGetScanHistoryQuery(scan.repository_id);
  const previousScore = (() => {
    if (!scanHistory) return null;
    const idx = scanHistory.findIndex((h) => h.id === scan.id);
    if (idx === -1 || idx === scanHistory.length - 1) return null;
    return scanHistory[idx + 1].debt_score;
  })();

  // Impact card popover details (Requirement 4)
  const getImpactDetails = (key: string) => {
    switch (key) {
      case 'remediation':
        return {
          title: `Estimated Remediation Cost (${fmtMoney(impact.remediationCost)})`,
          body: `Based on ${impact.fixableCount} AI-fixable findings across estimated developer effort and fix complexity. Calculated using an average senior engineer rate of $150/hr.`,
          actionLabel: 'View AI-fixable findings →',
          path: 'fixes',
        };
      case 'security':
        return {
          title: `Security Financial Exposure (${fmtMoney(impact.securityExposure)})`,
          body: `Based on ${secCount} security issues and ${cveCount} CVE dependencies evaluated against EPSS exploit probability and CVSS severity vectors.`,
          actionLabel: 'View security findings →',
          path: cveCount > 0 ? 'issues?category=dependencies' : 'issues?category=security',
        };
      case 'savings':
        return {
          title: `Automatable Savings (${fmtMoney(impact.automatedSavings)})`,
          body: `Based on ${impact.fixableCount} findings currently eligible for immediate automated AI fix generation and PR creation without manual code refactoring.`,
          actionLabel: 'View eligible findings →',
          path: 'fixes',
        };
      case 'debt_ratio':
        return {
          title: `Technical Debt Ratio (${scan.debt_ratio_pct?.toFixed(1) ?? '—'}%, Grade ${tdrGrade(scan.debt_ratio_pct)})`,
          body: `SQALE methodology: remediation cost (${fmtMoney(impact.remediationCost)}) divided by development cost — non-test lines of code × 30 minutes/line, SonarQube's published dev-cost convention. Lower is better; A ≤5%, B ≤10%, C ≤20%, D ≤50%, else E.`,
          actionLabel: 'View all findings →',
          path: 'issues',
        };
      default:
        return {
          title: `Total Issues (${totalIssueCount.toLocaleString()})`,
          body: `Complete dataset of all detected findings in current scan #${scan.id}: ${criticalCount} Critical, ${highCount} High, ${mediumCount} Medium, ${lowCount} Low.`,
          actionLabel: 'View all findings →',
          path: 'issues',
        };
    }
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', mb: 3, overflow: 'hidden', bgcolor: '#ffffff' }}>
      {/* Top accent bar */}
      <Box sx={{
        height: 4,
        background: scan.debt_score == null ? '#e5e7eb'
          : scan.debt_score >= 75 ? 'linear-gradient(90deg,#10b981,#34d399)'
            : scan.debt_score >= 50 ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
              : 'linear-gradient(90deg,#ef4444,#f87171)',
      }} />

      <Box sx={{ p: 2.5 }}>

        {/* ── 1. Top Section: Equal Height Unified Grid (Row 1: 30% / 35% / 35%) ── */}
        <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2.5 }}>

          {/* Card 1: Codebase Health (30% width) */}
          <Grid item xs={12} md={3.6}>
            <CodebaseHealthKPI
              score={scan.debt_score}
              previousScore={previousScore}
              onOpenScoreCalc={() => setScoreCalcOpen(true)}
            />
          </Grid>

          {/* Card 2: Health Dimensions & Radar (35% width - Clean 2 Column Structure) */}
          <Grid item xs={12} md={4.2}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  HEALTH DIMENSIONS
                </Typography>
                <Typography variant="caption" color="text.disabled" fontWeight={600}>
                  Click dimension to filter
                </Typography>
              </Box>

              {/* Clean Two-Column Layout: Left Dimension Rows, Right Vertically-Centered Radar */}
              <Grid container spacing={1.5} alignItems="center" sx={{ my: 'auto' }}>
                {/* Left: Dimension Values Stack */}
                <Grid item xs={6.5}>
                  <Stack spacing={0.75}>
                    {CATEGORIES.map(({ key, label, icon: Icon, issueKey, na, hideScore }) => {
                      const catScore = scan[key] as number | null;
                      const grade = scoreGrade(catScore);
                      const col = scoreColor(catScore);

                      if (na) {
                        return (
                          <Box
                            key={key}
                            sx={{
                              height: 34,
                              px: 1.25,
                              py: 0.5,
                              bgcolor: '#f8fafc',
                              borderRadius: 1.5,
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Icon sx={{ fontSize: 13, color: 'text.disabled' }} />
                              <Typography variant="caption" fontWeight={700} color="text.disabled" fontSize={11}>{label}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.disabled" fontWeight={600} fontSize={11}>N/A</Typography>
                          </Box>
                        );
                      }

                      return (
                        <Paper
                          key={key}
                          elevation={0}
                          onClick={() => onCategoryClick(issueKey)}
                          sx={{
                            height: 34,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: activeCategory === issueKey ? `${col}.main` : '#e2e8f0',
                            bgcolor: activeCategory === issueKey ? `${col}.50` : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: `${col}.main`, bgcolor: '#f8fafc', transform: 'translateX(2px)' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Icon sx={{ fontSize: 13, color: `${col}.main`, flexShrink: 0 }} />
                            <Typography variant="caption" fontWeight={700} color="text.primary" fontSize={11} noWrap>
                              {label}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 0.5 }}>
                            {hideScore ? (
                              <Typography variant="caption" color="success.main" fontWeight={700} fontSize={10}>Clean</Typography>
                            ) : (
                              <Typography variant="caption" fontWeight={800} color={`${col}.main`} fontSize={10}>
                                {catScore ?? 0}/100 ({grade})
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Grid>

                {/* Right: Radar Chart Vertically Centered */}
                <Grid item xs={5.5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <HealthRadar scan={scan} />
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Card 3: Business Impact Estimate (35% width) */}
          <Grid item xs={12} md={4.2}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  BUSINESS IMPACT ESTIMATE
                </Typography>
                <IconButton size="small" onClick={() => setMethodologyOpen(true)} title="View methodology">
                  <InfoOutlined fontSize="small" sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              <Grid container spacing={1.25} sx={{ my: 'auto' }}>
                {/* 1. Remediation Cost */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={(e) => setImpactPopoverAnchor({ element: e.currentTarget, cardKey: 'remediation' })}
                    sx={{
                      p: 1.5, bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 2, cursor: 'pointer',
                      height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      '&:hover': { boxShadow: '0 4px 12px rgba(124, 58, 237, 0.12)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <MonetizationOn sx={{ fontSize: 14, color: '#7c3aed' }} />
                      <Typography variant="caption" fontWeight={800} color="#7c3aed" fontSize={9} sx={{ textTransform: 'uppercase' }}>
                        Remediation Cost
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={900} color="#7c3aed" lineHeight={1}>
                      {fmtMoney(impact.remediationCost)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize={9} display="block" sx={{ mt: 0.5 }}>
                      Est. hours [View calc]
                    </Typography>
                  </Paper>
                </Grid>

                {/* 2. Security Exposure */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={(e) => setImpactPopoverAnchor({ element: e.currentTarget, cardKey: 'security' })}
                    sx={{
                      p: 1.5, bgcolor: '#fff5f5', border: '1px solid #fecaca', borderRadius: 2, cursor: 'pointer',
                      height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      '&:hover': { boxShadow: '0 4px 12px rgba(220, 38, 38, 0.12)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Shield sx={{ fontSize: 14, color: '#dc2626' }} />
                      <Typography variant="caption" fontWeight={800} color="#dc2626" fontSize={9} sx={{ textTransform: 'uppercase' }}>
                        Security Exposure
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={900} color="#dc2626" lineHeight={1}>
                      {fmtMoney(impact.securityExposure)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize={9} display="block" sx={{ mt: 0.5 }}>
                      {secCount} security · {cveCount} CVEs
                    </Typography>
                  </Paper>
                </Grid>

                {/* 3. Automatable Savings */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={(e) => setImpactPopoverAnchor({ element: e.currentTarget, cardKey: 'savings' })}
                    sx={{
                      p: 1.5, bgcolor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 2, cursor: 'pointer',
                      height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      '&:hover': { boxShadow: '0 4px 12px rgba(5, 150, 105, 0.12)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <TrendingDown sx={{ fontSize: 14, color: '#059669' }} />
                      <Typography variant="caption" fontWeight={800} color="#059669" fontSize={9} sx={{ textTransform: 'uppercase' }}>
                        Automatable Savings
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={900} color="#059669" lineHeight={1}>
                      {fmtMoney(impact.automatedSavings)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize={9} display="block" sx={{ mt: 0.5 }}>
                      {impact.fixableCount} fixable by AI agent
                    </Typography>
                  </Paper>
                </Grid>

                {/* 4. Total Issues */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={(e) => setImpactPopoverAnchor({ element: e.currentTarget, cardKey: 'total' })}
                    sx={{
                      p: 1.5, bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 2, cursor: 'pointer',
                      height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      '&:hover': { boxShadow: '0 4px 12px rgba(2, 132, 199, 0.12)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Equalizer sx={{ fontSize: 14, color: '#0284c7' }} />
                      <Typography variant="caption" fontWeight={800} color="#0284c7" fontSize={9} sx={{ textTransform: 'uppercase' }}>
                        Total Issues
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={900} color="#0284c7" lineHeight={1}>
                      {totalIssueCount.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize={9} display="block" sx={{ mt: 0.5 }}>
                      {criticalCount} critical · {highCount} high
                    </Typography>
                  </Paper>
                </Grid>

                {/* 5. Technical Debt Ratio (SQALE) — full width, pairs a % with an
                    industry-recognizable A-E grade rather than another dollar figure. */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    onClick={(e) => setImpactPopoverAnchor({ element: e.currentTarget, cardKey: 'debt_ratio' })}
                    sx={{
                      p: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                      '&:hover': { boxShadow: '0 4px 12px rgba(217, 119, 6, 0.12)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Scale sx={{ fontSize: 14, color: '#b45309' }} />
                      <Typography variant="caption" fontWeight={800} color="#b45309" fontSize={9} sx={{ textTransform: 'uppercase' }}>
                        Technical Debt Ratio (SQALE)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                      <Typography variant="h6" fontWeight={900} color={`${tdrColor(scan.debt_ratio_pct)}.main`} lineHeight={1}>
                        {scan.debt_ratio_pct !== null ? `${scan.debt_ratio_pct.toFixed(1)}%` : '—'}
                      </Typography>
                      <Typography variant="caption" fontWeight={800} color={`${tdrColor(scan.debt_ratio_pct)}.main`} fontSize={11}>
                        Grade {tdrGrade(scan.debt_ratio_pct)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* ── 2. Middle Section: Equal Height Top Risks + AI Assessment (Row 2: 37.5% / 62.5%) ── */}
        <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2.5 }}>

          {/* Top 3 Risks Card (Aligned height with AI Assessment) */}
          <Grid item xs={12} md={4.5}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: '#fde68a',
                borderRadius: 2.5,
                bgcolor: '#fffbeb',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmber color="warning" />
                    <Typography variant="subtitle2" fontWeight={900} color="#92400e">
                      TOP RISKS
                    </Typography>
                  </Box>
                  <Chip label="Scan Dataset" size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />
                </Box>

                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  {topCategories.length > 0 ? (
                    topCategories.slice(0, 3).map((cat, i) => (
                      <Box key={cat.key} sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #fcd34d' }}>
                        <Typography variant="body2" fontWeight={800} color={i === 0 ? 'error.main' : 'warning.dark'}>
                          {i === 0 ? '🔴' : '🟠'} {i + 1}. {cat.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                          {cat.count} {cat.label.toLowerCase()} finding{cat.count === 1 ? '' : 's'}. {cat.description}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No findings in this scan.
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Aligned CTA at bottom */}
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => onNavigate && onNavigate('issues')}
                sx={{ fontWeight: 800, textTransform: 'none', mt: 'auto', pt: 1 }}
              >
                View all risks in Actions →
              </Button>
            </Card>
          </Grid>

          {/* AI Assessment Card (Aligned height with Top Risks + Breathing Room) */}
          <Grid item xs={12} md={7.5}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2.5,
                bgcolor: '#ffffff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoFixHigh color="primary" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={900} color="text.primary">
                      AI ASSESSMENT
                    </Typography>
                  </Box>
                  <Chip label="Executive AI Synthesis" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                </Box>

                {/* Overall Observation — real per-scan synthesis if the backend produced one
                    (scan.ai_repo_summary), otherwise an honest unavailable state. Never
                    fabricated prose. See docs/scan-report-integrity-plan.md, KG-01. */}
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
                  <Typography variant="caption" fontWeight={800} color="text.secondary" display="block" sx={{ textTransform: 'uppercase', mb: 0.5, letterSpacing: 0.5 }}>
                    Overall Observation
                  </Typography>
                  {scan.ai_repo_summary ? (
                    <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                      {scan.ai_repo_summary}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                      The <strong>{repoName ?? 'this'}</strong> repository contains{' '}
                      <strong>{totalIssueCount.toLocaleString()} analyzed findings</strong>.{' '}
                      <Typography component="span" variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Narrative synthesis not yet available for this scan.
                      </Typography>
                    </Typography>
                  )}
                </Box>

                <Typography variant="caption" fontWeight={800} color="text.secondary" display="block" sx={{ textTransform: 'uppercase', mb: 1.25, letterSpacing: 0.5 }}>
                  Key Observations
                </Typography>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {topCategories.length > 0 ? (
                    topCategories.map((cat) => (
                      <Grid item xs={6} key={cat.key}>
                        <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#ffffff', height: '100%' }}>
                          <Typography variant="caption" fontWeight={800} color="text.primary" display="block" sx={{ mb: 0.25 }}>
                            • {cat.label} ({cat.count})
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                            {cat.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        No findings in this scan.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Aligned CTA at bottom */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                disableElevation
                onClick={() => onNavigate && onNavigate('issues')}
                sx={{ fontWeight: 800, textTransform: 'none', mt: 'auto', pt: 1, alignSelf: 'flex-start' }}
              >
                View supporting findings ({totalIssueCount.toLocaleString()}) →
              </Button>
            </Card>
          </Grid>
        </Grid>

        {/* ── 3. Bottom Section: Equal Width/Height "What Should I Do First?" (Row 3) ── */}
        <Card elevation={0} sx={{ p: 2.5, border: '1px solid #bbf7d0', borderRadius: 2.5, bgcolor: '#f0fdf4', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lightbulb color="success" />
              <Typography variant="subtitle2" fontWeight={900} color="#166534">
                WHAT SHOULD I DO FIRST?
              </Typography>
            </Box>
            <Typography variant="caption" color="success.dark" fontWeight={700}>
              Priorities based on severity, business impact, and AI priority scoring
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="stretch">
            {/* Priority 1 */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #cbd5e1',
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={800} color="error.main" display="block" sx={{ mb: 0.5 }}>
                    1. 🔴 Address Critical security findings
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                    {criticalVulnCount} Critical vulnerability findings requiring immediate remediation.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => onNavigate && onNavigate('issues?severity=critical&category=security')}
                  sx={{ fontWeight: 800, textTransform: 'none', mt: 'auto' }}
                >
                  View findings →
                </Button>
              </Paper>
            </Grid>

            {/* Priority 2 */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #cbd5e1',
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={800} color="warning.dark" display="block" sx={{ mb: 0.5 }}>
                    2. 🟠 Reduce high-complexity areas
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                    {highCount} High-severity findings impacting maintainability & testability.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => onNavigate && onNavigate('issues?severity=high')}
                  sx={{ fontWeight: 800, textTransform: 'none', mt: 'auto' }}
                >
                  View findings →
                </Button>
              </Paper>
            </Grid>

            {/* Priority 3 */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #cbd5e1',
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={800} color="info.main" display="block" sx={{ mb: 0.5 }}>
                    3. 🟡 Address performance opportunities
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                    {scan.carbon_co2g_total
                      ? `Optimization opportunities estimated to save up to ${(scan.carbon_co2g_total / 1000).toFixed(1)} kg CO₂/mo.`
                      : 'Carbon impact not estimated for this scan.'}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => onNavigate && onNavigate('savings')}
                  sx={{ fontWeight: 800, textTransform: 'none', mt: 'auto' }}
                >
                  View Performance →
                </Button>
              </Paper>
            </Grid>

            {/* Priority 4 */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #cbd5e1',
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={800} color="primary.main" display="block" sx={{ mb: 0.5 }}>
                    4. 🔵 Refactor oversized files
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                    Monolithic files with over 1,000 LOC recommended for AI modular split.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => onNavigate && onNavigate('refactor')}
                  sx={{ fontWeight: 800, textTransform: 'none', mt: 'auto' }}
                >
                  View Refactor →
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Card>

        {/* ── 4. Footer Link: Assessment Methodology (Requirement 8) ── */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<InfoOutlined fontSize="small" />}
            onClick={() => setMethodologyOpen(true)}
            sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'none', fontSize: 12 }}
          >
            ⓘ Methodology & assumptions
          </Button>
        </Box>

      </Box>

      {/* ── Dialog 1: How Score is Calculated Modal ── */}
      <Dialog open={scoreCalcOpen} onClose={() => setScoreCalcOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Codebase Health Score Calculation
          <Chip label={`${scan.debt_score ?? 0} / 100`} color="primary" size="small" sx={{ fontWeight: 800 }} />
        </DialogTitle>

        <DialogContent dividers>
          {scan.score_formula ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                The Codebase Health Score is a weighted index (0–100) across {Object.keys(scan.score_formula.weights).length} dimensions. Weights and dimensions come directly from the scoring engine — this dialog can't drift from the real formula.
              </Typography>

              <Stack spacing={2} sx={{ mb: 3 }}>
                {Object.entries(scan.score_formula.weights)
                  .sort(([, a], [, b]) => b - a)
                  .map(([key, weight]) => {
                    const meta = FORMULA_DIMENSION_META[key];
                    const value = meta ? (scan[meta.scoreField] as number | null) : null;
                    return (
                      <Box key={key} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                            {meta?.label ?? key} ({Math.round(weight * 100)}% Weight)
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={900}>
                            {value ?? 0} / 100 (Grade {scoreGrade(value ?? null)})
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {meta?.description ?? 'Contributes to the weighted score.'}
                        </Typography>
                      </Box>
                    );
                  })}

                {/* Coverage is reported for context but is not part of the weighted formula —
                    shown separately so it isn't mistaken for a weighted dimension. */}
                <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={800} color="text.secondary">
                      Test Coverage (not weighted)
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={900} color="text.disabled">
                      {scan.score_coverage ?? 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.disabled">
                    Reported separately — not measurable via static analysis alone, and not currently part of the weighted score.
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ p: 1.5, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                <Typography variant="caption" color="#1e40af" fontWeight={700} display="block">
                  Formula (v{scan.score_formula.version}):
                </Typography>
                <Typography variant="caption" color="#1e40af" sx={{ fontFamily: 'monospace' }}>
                  Score ={' '}
                  {Object.entries(scan.score_formula.weights)
                    .map(([key, weight]) => `(${FORMULA_DIMENSION_META[key]?.label ?? key} × ${weight})`)
                    .join(' + ')}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Scoring methodology not available for this scan.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setScoreCalcOpen(false)} variant="contained" disableElevation sx={{ fontWeight: 800 }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog 2: Methodology & Assumptions Modal ── */}
      <Dialog open={methodologyOpen} onClose={() => setMethodologyOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlined color="primary" />
          Assessment Methodology & Assumptions
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={800} color="primary.main" sx={{ mb: 1 }}>
                  1. Health Score Index
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Combines SAST vulnerability rules, AST cyclomatic complexity analysis, and documentation completeness into a normalized 0–100 scale.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={800} color="#7c3aed" sx={{ mb: 1 }}>
                  2. Remediation Cost Estimate ({fmtMoney(impact.remediationCost)})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Calculated from each finding's AI-estimated fix time (falling back to a severity-based estimate for any finding this scan's AI analysis hasn't reached yet) multiplied by a standard $150/hr senior developer rate.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={800} color="#dc2626" sx={{ mb: 1 }}>
                  3. Security Exposure ({fmtMoney(impact.securityExposure)})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Derived from EPSS (Exploit Prediction Scoring System) probability scores and CISA KEV (Known Exploited Vulnerabilities) database classifications.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={800} color="#059669" sx={{ mb: 1 }}>
                  4. Automatable Savings ({fmtMoney(impact.automatedSavings)})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Financial savings unlocked by using KodeGauge AI-assisted automated fix generation and pull request workflows instead of manual refactoring.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f0fdf4', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={800} color="#166534" sx={{ mb: 1 }}>
                  5. Carbon Impact Estimation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Estimates kWh electricity savings and CO₂ emission reductions resulting from removing redundant string concatenations, unclosed streams, and O(n²) nested loop CPU cycles.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fffbeb', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={800} color="#b45309" sx={{ mb: 1 }}>
                  6. Technical Debt Ratio ({scan.debt_ratio_pct?.toFixed(1) ?? '—'}%)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  SQALE methodology: remediation cost ÷ development cost, where development cost is non-test lines of code × 30 minutes/line — SonarQube's published dev-cost convention, not an invented multiplier. Graded A (≤5%) through E (&gt;50%), same bucketing SonarQube uses for its Maintainability Rating.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setMethodologyOpen(false)} variant="contained" disableElevation sx={{ fontWeight: 800 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Popover: Impact Card Details ── */}
      {impactPopoverAnchor && (
        <Popover
          open={Boolean(impactPopoverAnchor)}
          anchorEl={impactPopoverAnchor.element}
          onClose={() => setImpactPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{ sx: { p: 2, maxWidth: 320, borderRadius: 2 } }}
        >
          {(() => {
            const info = getImpactDetails(impactPopoverAnchor.cardKey);
            return (
              <Box>
                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                  {info.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, lineHeight: 1.5 }}>
                  {info.body}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  fullWidth
                  disableElevation
                  onClick={() => {
                    setImpactPopoverAnchor(null);
                    if (onNavigate) onNavigate(info.path);
                  }}
                  sx={{ fontWeight: 800, textTransform: 'none' }}
                >
                  {info.actionLabel}
                </Button>
              </Box>
            );
          })()}
        </Popover>
      )}

    </Card>
  );
};

export default ExecutiveSummary;
